/**
 * Matches service for managing user matches and chat creation
 */

import chatService from "@features/messaging/services/chatService";
import type { Match } from "@features/messaging/types";
import { getChatIdForPair } from "@features/messaging/utils/chatParticipants";
import type { Chat, User } from "@fitties/shared";
import { backendRegistry } from "@infrastructure/providers/backendRegistry";
import logger from "@logger";

import { fetchPublicUserProfiles } from "@shared/services/publicUserProfilesService";

/**
 * Matches service class
 */
export class MatchesService {
  /**
   * Get all mutual matches for a user.
   * Derives chat IDs from chat_partner_uids using getChatIdForPair — no stored map needed.
   */
  async getAllMatches(userId: string): Promise<Match[]> {
    try {
      logger.info("Fetching all matches", { userId });

      // Get current user data
      const currentUser = await backendRegistry.database.getDocument<User>("users", userId);
      if (!currentUser) {
        throw new Error("Current user not found");
      }

      const partnerUids = currentUser.chat_partner_uids ?? [];
      const chatIds = partnerUids.map((partnerUid) => getChatIdForPair(userId, partnerUid));

      const [partnerUsers, chats] = await Promise.all([
        fetchPublicUserProfiles(partnerUids),
        Promise.all(chatIds.map((id) => backendRegistry.database.getDocument<Chat>("chats", id))),
      ]);

      const partnersByUid = new Map(partnerUsers.map((u) => [u.uid, u]));

      const allMatches: Match[] = partnerUids
        .map((partnerUid, i) => {
          const partnerUser = partnersByUid.get(partnerUid);
          if (!partnerUser) {
            return null;
          }
          const chat = chats[i];
          const chatId = chatIds[i];
          return {
            user: partnerUser,
            isLocked: false,
            ...(chat && chatId && { chat: { ...chat, id: chatId } }),
          };
        })
        .filter((match): match is Match => match !== null);

      logger.info("All matches fetched successfully", {
        userId,
        totalMatchCount: allMatches.length,
      });

      return allMatches;
    } catch (error) {
      logger.error("Error fetching all matches:", error);
      throw error;
    }
  }

  /**
   * Create a chat for a new match (delegates to chat service)
   */
  async createChatForMatch(userId1: string, userId2: string): Promise<string> {
    return chatService.createChatForMatch(userId1, userId2);
  }

  /**
   * Mark match as read
   */
  async markMatchAsRead(chatId: string, userId: string): Promise<void> {
    try {
      logger.info("Marking match as read", { chatId, userId });

      const chat = await backendRegistry.database.getDocument<Chat>("chats", chatId);
      if (!chat) {
        throw new Error("Chat not found");
      }

      const seenBy = chat.last_message_seen_by_uids ?? [];
      if (!seenBy.includes(userId)) {
        const updated = [...seenBy, userId];
        await backendRegistry.database.updateDocument("chats", chatId, {
          last_message_seen_by_uids: updated,
        });
      }

      logger.info("Match marked as read successfully", { chatId, userId });
    } catch (error) {
      logger.error("Error marking match as read:", error);
      throw error;
    }
  }
}

// Export singleton instance
export const matchesService = new MatchesService();
export default matchesService;
